import { forwardRef, useId } from 'react';
import clsx from 'clsx';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

const Select = forwardRef(({
  label,
  error,
  hint,
  required,
  options,
  placeholder,
  className,
  selectClassName,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  ...props
}, ref) => {
  const reactId = useId();
  const selectId = id || `select-${reactId}`;
  const errorId = `${selectId}-error`;
  const hintId = `${selectId}-hint`;
  const describedBy = clsx(ariaDescribedBy, hint && hintId, error && errorId) || undefined;

  return (
    <div className={className}>
      {label && (
        <label htmlFor={selectId} className="block text-sm font-medium text-slate-700 mb-1.5">
          {label}
          {required && !String(label).includes('*') && (
            <span aria-hidden="true" className="ml-0.5 font-normal text-danger-500">*</span>
          )}
        </label>
      )}
      {/* The native arrow is a grey OS glyph that matches nothing else in the kit
          and leaves a menu looking identical to a text input. */}
      <div className="relative">
        <select
          ref={ref}
          id={selectId}
          required={required}
          aria-label={!label ? ariaLabel || placeholder : undefined}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={describedBy}
          className={clsx(
            'input-field appearance-none cursor-pointer pr-10',
            error && 'border-danger-500 focus:ring-danger-500 focus:border-danger-500',
            // Consumer className is merged last so it wins on class-attribute order.
            selectClassName
          )}
          {...props}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map(opt => (
            <option key={opt.value ?? opt} value={opt.value ?? opt}>
              {opt.label ?? opt}
            </option>
          ))}
        </select>
        <ChevronDownIcon
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500"
          aria-hidden="true"
        />
      </div>
      {hint && <p id={hintId} className="mt-1.5 text-sm text-slate-500">{hint}</p>}
      {error && (
        <div role="alert">
          <p id={errorId} className="mt-1.5 text-sm font-medium text-danger-700">{error}</p>
        </div>
      )}
    </div>
  );
});

Select.displayName = 'Select';
export default Select;
